import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FormattedMarkdown } from '@/utils/markdownFormatter';

import { useScrollFadeMask } from '@/hooks/useScrollFade';
import { resolveHelpEntry } from '@/components/ui/help-registry';

interface HelpView {
  fieldName: string;
  content: string;
  variables?: Record<string, unknown>;
  injectableComponents?: Record<string, React.ReactNode>;
}

interface InlineHelpContextValue {
  showHelp: (
    fieldName: string,
    content: string,
    variables?: Record<string, unknown>,
    injectableComponents?: Record<string, React.ReactNode>,
  ) => void;
  isHelpOpen: boolean;
}

const InlineHelpContext = createContext<InlineHelpContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useInlineHelp = () => useContext(InlineHelpContext);

interface InlineHelpProviderProps {
  children: React.ReactNode;
}

const slideVariants = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? -20 : 20,
    opacity: 0,
  }),
};

const TRANSITION = { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const };

export const InlineHelpProvider: React.FC<InlineHelpProviderProps> = ({ children }) => {
  const [stack, setStack] = useState<HelpView[]>([]);
  const directionRef = useRef<1 | -1>(1);
  const scrollFadeRef = useScrollFadeMask();

  const helpView = stack.length > 0 ? stack[stack.length - 1] : null;

  const showHelp = useCallback((
    fieldName: string,
    content: string,
    variables?: Record<string, unknown>,
    injectableComponents?: Record<string, React.ReactNode>,
  ) => {
    directionRef.current = 1;
    setStack([{ fieldName, content, variables, injectableComponents }]);
  }, []);

  const goBack = useCallback(() => {
    directionRef.current = -1;
    setStack(prev => prev.slice(0, -1));
  }, []);

  const isHelpOpen = helpView !== null;

  // Set up window.navigateHelp for deep links in inline help content
  useEffect(() => {
    if (!isHelpOpen) return;
    (window as unknown as Record<string, unknown>).navigateHelp = (targetFieldName: string) => {
      const entry = resolveHelpEntry(targetFieldName);
      if (!entry) return;
      directionRef.current = 1;
      setStack(prev => [...prev, { fieldName: targetFieldName, ...entry }]);
    };
    return () => { delete (window as unknown as Record<string, unknown>).navigateHelp; };
  }, [isHelpOpen]);

  const ctxValue = useMemo(() => ({ showHelp, isHelpOpen }), [showHelp, isHelpOpen]);

  return (
    <InlineHelpContext.Provider value={ctxValue}>
      <AnimatePresence mode="popLayout" custom={directionRef.current} initial={false}>
        {helpView ? (
          <motion.div
            key={`help-${helpView.fieldName}`}
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={TRANSITION}
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="pb-3 mb-1 relative">
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors mb-1.5"
              >
                <ArrowLeft className="w-3 h-3" />
                Back
              </button>
              <h3 className="font-heading uppercase tracking-[0.08em] text-base font-semibold text-foreground">
                {helpView.fieldName}
              </h3>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-[var(--border)]" />
              <div className="absolute bottom-0 left-0 h-[2px] w-10 bg-primary/60" />
            </div>
            <div
              ref={scrollFadeRef}
              className="overflow-y-auto flex-1 min-h-0 scroll-fade-y"
            >
              <FormattedMarkdown
                content={helpView.content}
                variables={helpView.variables}
                injectableComponents={helpView.injectableComponents}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={TRANSITION}
            className="flex flex-col flex-1 min-h-0"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </InlineHelpContext.Provider>
  );
};
